<template>
  <form @submit.prevent="save" @click.stop="">
    <div class="dialog-header">
      <h1>{{ title }}</h1>
    </div>
    <div class="dialog-content">
      <template v-if="categories.length === 0">
        <p>
          Voordat u een pakket aan kunt maken dient u eerst een nieuwe categorie
          aan te maken.
        </p>
        <div class="button-holder">
          <button type="button" @click="$emit('close')" class="pill-button">
            sluiten
          </button>
        </div>
      </template>
      <template v-else>
        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="name">Naam</label>
            <div>
              <input
                id="name"
                type="text"
                v-model="productPackage.name"
                name="name"
                ref="name"
                placeholder="Geef het pakket een herkenbare naam"
                :class="{ invalid: $v.productPackage.name.$invalid }"
                @input="$v.productPackage.$touch"
              />
            </div>
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="publisher">Categorie</label>
            <div>
              <popup-menu class="popup" anchor-position="top">
                <span slot="trigger"
                  ><span
                    class="placeholder invalid"
                    v-if="!productPackage.category"
                    >Selecteer een categorie</span
                  >
                  <span v-else class="category">{{
                    productPackage.category
                  }}</span
                  ><button
                    class="chevron down"
                    :class="{ invalid: !productPackage.category }"
                  ></button
                ></span>
                <menu-item
                  v-for="category in categories"
                  :key="category.id"
                  @click="setCategory(category)"
                  >{{ category.name }}
                </menu-item>
              </popup-menu>
            </div>
          </div>
        </div>

        <div class="field-wrapper">
          <div class="field-100 label-fix">
            <label for="code">Productcode</label>
            <div>
              <input
                id="code"
                type="text"
                v-model="productPackage.productCode"
                name="code"
                ref="code"
                placeholder="Productcode"
                @input="$v.productPackage.$touch"
              />
            </div>
          </div>
        </div>

        <div class="select-wrapper">
          <select-box
            @action="setHidden()"
            :selected="!productPackage.active"
          ></select-box>

          <span class="pointer" @click="setHidden()">Dit pakket verbergen</span>
        </div>

        <div class="select-wrapper">
          <select-box
            @action="setMaxAmount()"
            :selected="productPackage.maxOrderAmount > 0"
          ></select-box>

          <span class="pointer" @click="setMaxAmount()"
            >Maximaal 1 te bestellen</span
          >
        </div>

        <div class="field-wrapper description">
          <div class="field-100 label-fix">
            <label for="description">Beschrijving</label>
            <div>
              <textarea
                id="description"
                type="text"
                v-model="productPackage.description"
                name="description"
                placeholder="Voeg een heldere beschrijving van het pakket toe"
                @input="$v.productPackage.$touch"
                :class="{ invalid: $v.productPackage.description.$invalid }"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="field-wrapper preview">
          <div class="field-100 label-fix">
            <label>Preview</label>
            <span
              v-if="productPackage.previewImage"
              class="preview-remove-button"
            >
              <close-button @click="removePreview" color="white"></close-button>
            </span>
            <section class="preview-container">
              <img
                v-if="productPackage.previewImage"
                class="preview-image"
                :src="`/media/print-store/${rootStore.user.printStore.id}/preview/${productPackage.previewImage}/300`"
              />
              <print-store-file-upload
                v-show="showPreviewUpload"
                caption="afbeelding toevoegen"
                @uploadComplete="onUploadComplete($event)"
                @uploadStart="onUploadStart"
                :is-preview="true"
                class="margin-correction"
              ></print-store-file-upload>
              <bouncing-spinner v-if="uploadingPreview"></bouncing-spinner>
            </section>
          </div>
        </div>

        <section class="products" style="margin: 0 0 4px;">
          <header>
            <label>Ontwerpen</label>
            <div class="">
              <button
                class="main-button margin-correction"
                @click.prevent="showProducts"
              >
                toevoegen
              </button>
              <toggle
                class="toggle"
                @action="toggleSelectAll"
                :active="allSelected"
                v-if="productPackage.products.length > 0"
              ></toggle>
            </div>
          </header>
          <div class="product-holder">
            <article
              v-for="(product, index) in productPackage.products"
              :key="index"
            >
              <product
                :product="product"
                @click="selectProduct(product, index)"
                @selectBox="selectProduct(product, index)"
                :selected="selectedProductsIndex.indexOf(index) > -1"
                :show-select-box="true"
                :show-specs="true"
              >
                <template v-slot:menu>
                  <popup-menu
                    anchor-position="right"
                    v-show="
                      selectedProductsIndex.includes(index) ||
                      selectedProductsIndex.length < 1
                    "
                  >
                    <button class="kebab-btn" slot="trigger">
                      <span></span>
                    </button>
                    <template
                      v-slot:title
                      v-if="selectedProductsIndex.length > 1"
                      >{{ selectedProductsIndex.length }} geselecteerde
                      ontwerpen
                    </template>
                    <menu-item
                      @click="modifyProducts(product, index)"
                      :disabled="multipleProductTypesSelected"
                      >Specificaties wijzigen
                    </menu-item>
                    <menu-item @click="removeProduct(index)"
                      >Uit pakket verwijderen
                    </menu-item>
                  </popup-menu>
                </template></product
              >
            </article>
          </div>
        </section>
        <div class="button-holder">
          <button
            class="pill-button secondary cancel"
            type="button"
            @click="$emit('close')"
          >
            annuleren
          </button>
          <button
            id="confirm-button"
            class="pill-button"
            type="submit"
            v-show="!saving"
            :disabled="saveButtonDisabled()"
          >
            {{ saveLabel }}
          </button>
        </div>
      </template>
    </div>
  </form>
</template>
<script>
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'
import { addPackage, updatePackage } from '@/features/printstore/PrintStoreApi'
import { uuidv4 } from '@/api/uuid'
import PrintStoreFileUpload from '@/features/printstore/PrintStoreFileUpload'
import ProductSelector from '@/features/printstore/dialogs/ProductSelector/ProductSelector'
import Product from '@/features/printstore/dialogs/ProductSelector/Product'
import { EventBus, Events } from '@/eventbus/EventBus'
import ProductSpecs from '@/features/printstore/dialogs/ProductSpecs'
import { required } from '@vuelidate/validators'
import Toggle from '../../../components/ui/Toggle.vue'
import { logger } from '@/logger'

export default {
  props: ['package', 'callback'],
  data() {
    return {
      saving: false,
      productPackage: {
        active: true,
        name: undefined,
        productCode: '',
        category: '',
        categoryId: undefined,
        description: '',
        products: [],
        previewImage: undefined,
        maxOrderAmount: undefined
      },
      categories: [],
      isUpdate: false,
      title: 'Nieuw pakket',
      saveLabel: 'CREËREN',
      uploadingPreview: false,
      showPreviewUpload: false,
      selectedProductsIndex: [],
      selectedProductTypes: []
    }
  },
  components: {
    PrintStoreFileUpload,
    Product,
    Toggle
  },
  validations: {
    productPackage: {
      name: {
        required
      },
      category: {
        required
      },
      description: {
        required
      }
    }
  },
  mounted() {
    if (this.$refs.name) {
      this.$refs.name.focus()
    }
    this.categories = this.printStoreStore.categories

    if (this.package) {
      this.productPackage = {
        products: [],
        ...this.package,
        category: this.categories.find(
          (category) => category.id === this.package.categoryId
        ).name
      }
      this.isUpdate = true
      this.title = 'Pakket wijzigen'
      this.saveLabel = 'Toepassen'
    } else if (this.$route.params.categoryId) {
      let cat = this.categories.find(
        (category) => category.id === this.$route.params.categoryId
      )
      this.productPackage.category = cat.name
      this.productPackage.categoryId = cat.id
    }

    this.showPreviewUpload = this.productPackage.previewImage === undefined
  },
  unmounted() {
    this.printStoreStore.setCollectionFilter({
      id: '0',
      name: 'Alles'
    })
  },
  methods: {
    saveButtonDisabled() {
      return (
        this.$v.productPackage.$invalid || !this.$v.productPackage.$anyDirty
      )
    },
    setCategory(category) {
      this.productPackage.category = category.name
      this.productPackage.categoryId = category.id
      this.$v.productPackage.category.$touch()
    },
    save() {
      const model = {
        id: this.isUpdate ? this.productPackage.id : uuidv4(),
        name: this.productPackage.name,
        description: this.productPackage.description,
        categoryId: this.productPackage.categoryId,
        previewImage: this.productPackage.previewImage,
        products: this.productPackage.products,
        printStoreId: this.productPackage.printStoreId,
        productCode: this.productPackage.productCode,
        active: this.productPackage.active,
        maxOrderAmount: this.productPackage.maxOrderAmount
      }

      if (this.isUpdate) {
        updatePackage(model)
          .then(() => {
            this.callback(model)
            EventBus.emit(Events.CATEGORY_CONTENT_UPDATED)
            this.$emit('close')
          })
          .catch((error) => logger.error(error))
      } else {
        addPackage(model)
          .then(() => {
            if (this.callback) this.callback(model)
            EventBus.emit(Events.CATEGORY_CONTENT_UPDATED)
            this.$emit('close')

            this.$router.push({
              name: 'printStoreCategory',
              params: {
                categoryId: model.categoryId
              }
            })
          })
          .catch((error) => logger.error(error))
      }
    },
    onUploadStart() {
      this.uploadingPreview = true
      this.showPreviewUpload = false
    },
    onUploadComplete(message) {
      this.productPackage.previewImage = message.uuid
      this.uploadingPreview = false
      this.$forceUpdate()
      this.$v.productPackage.$touch()
    },
    addProducts(products) {
      this.productPackage.products.push(...products)
      this.$v.productPackage.$touch()
    },

    removeProduct(i) {
      if (this.selectedProductsIndex.length === 0) {
        this.productPackage.products.splice(i, 1)
      } else {
        //multiple products to remove
        this.productPackage.products = this.productPackage.products.filter(
          (value, index) => {
            return this.selectedProductsIndex.indexOf(index) === -1
          }
        )
      }

      this.selectedProductsIndex = []
      this.selectedProductTypes = []
      this.$v.productPackage.$touch()
    },
    showProducts() {
      this.$modal.show(
        ProductSelector,
        {
          callback: this.addProducts,
          packageProducts: this.productPackage.products
        },
        {
          adaptive: true,
          width: 410
        }
      )
    },
    updateProducts(productSpecs, prodIndex) {
      if (prodIndex === undefined) {
        this.selectedProductsIndex.forEach((index) => {
          this.productPackage.products[index].quantity = productSpecs[0]
          this.productPackage.products[index].printSpecification =
            productSpecs[1]

          if (productSpecs[2]) {
            this.productPackage.products[index].variant = { ...productSpecs[2] }
            this.productPackage.products[index].variant.previewImage = index

            this.productPackage.products[
              index
            ].variant.previewImage = this.productPackage.products[
              index
            ].previews.find((prev) => {
              return prev.colour == productSpecs[2].color
            }).uuid
          }
        })
      } else {
        this.productPackage.products[prodIndex] = productSpecs
      }
      this.selectedProductsIndex = []
      this.selectedProductTypes = []
      this.$forceUpdate()
      this.$v.productPackage.$touch()
    },
    modifyProducts(product, index) {
      let products = []
      if (this.selectedProductsIndex.includes(index)) {
        this.selectedProductsIndex.forEach((index) => {
          products.push(this.productPackage.products[index])
        })
      } else {
        products.push(product)
      }

      this.$modal.show(
        ProductSpecs,
        {
          products: products,
          productIndex: index,
          buttonLabel: 'specificaties wijzigen',
          callback: this.updateProducts
        },
        {
          adaptive: true,
          width: 363
        }
      )
    },
    removePreview() {
      this.productPackage.previewImage = undefined
      this.showPreviewUpload = true
      this.$v.productPackage.$touch()
    },
    selectProduct(product, index) {
      if (this.selectedProductsIndex.indexOf(index) < 0) {
        this.selectedProductsIndex.push(index)
        this.selectedProductTypes.push(product.productType)
      } else {
        this.selectedProductsIndex.splice(
          this.selectedProductsIndex.indexOf(index),
          1
        )
        this.selectedProductTypes.splice(
          this.selectedProductTypes.indexOf(product.productType),
          1
        )
      }
    },
    toggleSelectAll() {
      if (
        this.selectedProductsIndex.length ===
        this.productPackage.products.length
      ) {
        this.selectedProductsIndex = []
        this.selectedProductTypes = []
      } else {
        this.selectedProductsIndex = []
        this.productPackage.products.forEach((prod, index) => {
          this.selectedProductsIndex.push(index)
          this.selectedProductTypes.push(prod.productType)
        })
      }
    },
    setHidden() {
      this.productPackage.active = !this.productPackage.active
      this.$v.productPackage.$touch()
    },
    setMaxAmount() {
      if (this.productPackage.maxOrderAmount > 0) {
        this.productPackage.maxOrderAmount = 0
      } else {
        this.productPackage.maxOrderAmount = 1
      }

      this.$v.productPackage.$touch()
    }
  },
  computed: {
    ...mapStores(usePrintStoreStore),
    multipleProductTypesSelected() {
      return !this.selectedProductTypes.every(
        (v) => v === this.selectedProductTypes[0]
      )
    },
    allSelected() {
      return (
        this.selectedProductsIndex.length ===
          this.productPackage.products.length &&
        this.productPackage.products.length > 0
      )
    }
  }
}
</script>
<style lang="scss" scoped>
form {
  height: 100%;
  position: relative;
  overflow-y: auto;
}

.chevron {
  color: var(--sub-color);
  padding-left: 8px;
  vertical-align: top;

  &.invalid {
    color: var(--error-color);
  }
}

.popup {
  display: inline-block;
  vertical-align: baseline;
}

label {
  width: 85px;
}

.category {
  color: var(--sub-color);
  font-weight: 500;
}

.margin-correction {
  margin-left: -5px;
}

textarea {
  width: 100%;
  height: 60px;
  line-height: 18px;
}

.preview-button {
  float: left;
  margin-top: -1px;
}

.preview {
  position: relative;
  label {
    margin-top: 3px;
  }

  .preview-container {
    float: left;
    position: relative;
    width: 150px;

    img {
      max-height: 141px;
      max-width: 100%;
    }
  }
}

.remove-btn {
  margin-left: 5px;
}

.products {
  margin: 0;

  header {
    margin-bottom: 10px;
  }

  main {
    article {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 5px;
      margin-bottom: 5px;

      svg {
        margin-top: -10px;
        margin-left: 10px;
      }
    }
  }
}

.description {
  margin-bottom: 0;
}

.products {
  min-height: 200px;

  label {
    margin-top: 1px;
  }
}

.toggle {
  float: right;
}

.cancel {
  margin-right: 5px;
}
.select-wrapper {
  margin: 5px 0 15px;
}

.product-holder {
  max-height: 500px;
  overflow-y: auto;
  margin-bottom: 25px;
}
</style>
